import { Action, createHook, createStore } from "react-sweet-state";

import asyncOrSwim from "utility/async_or_swim";
import {
  authenticatedDeleteFetch,
  authenticatedGetFetch,
  authenticatedPatchFetch,
  authenticatedPostFetch,
} from "utility/authenticated_fetch";
import { captureSentryException } from "utility/capture_sentry_exception";

import { ActionState } from "./lib";

// -----------------------------------------------------------------------------

const initialState: IgnoreStoreState = {
  ignoreRules: [],

  ignoreState: ActionState.INITIAL,
  previewIgnoreState: ActionState.INITIAL,
  unignoreState: ActionState.INITIAL,
  bulkIgnoreState: ActionState.INITIAL,
  bulkUnignoreState: ActionState.INITIAL,
  listRulesState: ActionState.INITIAL,
  editIgnoreRuleState: ActionState.INITIAL,
  deleteIgnoreRuleState: ActionState.INITIAL,
};

// -----------------------------------------------------------------------------

export const useIgnoreStore = createHook(
  createStore({
    initialState,
    actions: {
      ignoreFinding,
      previewIgnore,
      unignoreFinding,
      bulkIgnoreFinding,
      bulkUnignoreFinding,
      listIgnoreRules,
      editIgnoreRule,
      deleteIgnoreRule,
    },
  })
);

// -----------------------------------------------------------------------------

function ignoreFinding({
  uuid,
  ignoreType,
  ruleType,
  assetRegex,
  ignoreReason,
  onSuccess,
  onError,
}: IgnoreParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().ignoreState === ActionState.IN_PROGRESS) {
      return;
    }

    try {
      setState({
        ignoreState: ActionState.IN_PROGRESS,
      });
      await authenticatedPostFetch(
        "api/findings/ignore/create",
        getCreateData({
          uuid,
          ignoreType,
          ruleType,
          assetRegex,
          ignoreReason,
        })
      );
      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to ignore finding");
      onError();
    }
    setState({
      ignoreState: ActionState.INITIAL,
    });
  };
}

function previewIgnore({
  uuid,
  ignoreType,
  ruleType,
  assetRegex,
  ignoreReason,
  onSuccess,
  onError,
}: IgnoreParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().previewIgnoreState === ActionState.IN_PROGRESS) {
      return;
    }

    try {
      setState({
        previewIgnoreState: ActionState.IN_PROGRESS,
      });
      const responsePayload = await authenticatedPostFetch(
        "api/findings/ignore/create_preview",
        getCreateData({
          uuid,
          ignoreType,
          ruleType,
          assetRegex,
          ignoreReason,
        })
      );
      onSuccess(responsePayload.results);
    } catch (error) {
      captureSentryException(error, "Failed to create ignore finding preview");
      onError();
    }
    setState({
      previewIgnoreState: ActionState.INITIAL,
    });
  };
}

function unignoreFinding({
  uuid,
  onSuccess,
  onError,
}: UnignoreParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().unignoreState === ActionState.IN_PROGRESS) {
      return;
    }

    try {
      setState({
        unignoreState: ActionState.IN_PROGRESS,
      });
      await authenticatedDeleteFetch(`api/findings/single_unignore/${uuid}`);
      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to unignore finding");
      onError();
    }
    setState({
      unignoreState: ActionState.INITIAL,
    });
  };
}

function bulkIgnoreFinding({
  uuids,
  ignoreReason,
  onSuccess,
  onError,
}: BulkIgnoreParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().bulkIgnoreState === ActionState.IN_PROGRESS) {
      return;
    }

    setState({
      bulkIgnoreState: ActionState.IN_PROGRESS,
    });

    const errors: Error[] = [];
    uuids.forEach((uuid) =>
      asyncOrSwim(
        async () =>
          await authenticatedPostFetch(
            "api/findings/ignore/create",
            getCreateData({
              uuid,
              ignoreType: "instance",
              ruleType: "",
              assetRegex: "",
              ignoreReason,
            })
          ),
        (error) => errors.push(error)
      )
    );

    if (errors.length > 0) {
      captureSentryException(errors, "Failed to bulk ignore findings");
      onError(errors.length);
    }

    onSuccess();
    setState({
      bulkIgnoreState: ActionState.INITIAL,
    });
  };
}

function bulkUnignoreFinding({
  uuids,
  onSuccess,
  onError,
}: BulkUnignoreParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().bulkUnignoreState === ActionState.IN_PROGRESS) {
      return;
    }

    //
    setState({
      bulkUnignoreState: ActionState.IN_PROGRESS,
    });

    const errors: Error[] = [];
    uuids.forEach((uuid) =>
      asyncOrSwim(
        async () =>
          await authenticatedDeleteFetch(
            `api/findings/single_unignore/${uuid}`
          ),
        (error) => errors.push(error)
      )
    );

    if (errors.length > 0) {
      captureSentryException(errors, "Failed to bulk unignore findings");
      return onError(errors.length);
    }

    onSuccess();
    setState({
      bulkUnignoreState: ActionState.INITIAL,
    });
  };
}

function listIgnoreRules(): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().listRulesState === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({
        listRulesState: ActionState.IN_PROGRESS,
      });
      const responsePayload = await authenticatedGetFetch(
        "api/findings/ignore/list"
      );
      setState({
        listRulesState: ActionState.COMPLETED,
        ignoreRules: responsePayload.results,
      });
    } catch (error) {
      setState({
        listRulesState: ActionState.ERROR,
      });
      captureSentryException(error, "Failed to get ignore rules");
    }
  };
}

function editIgnoreRule({
  uuid,
  newReason,
  onSuccess,
  onError,
}: EditIgnoreRuleParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().editIgnoreRuleState === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({
        editIgnoreRuleState: ActionState.IN_PROGRESS,
      });
      await authenticatedPatchFetch(`api/findings/ignore/${uuid}`, {
        ignore_reason: newReason,
      });
      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to edit ignore rule");
      onError();
    }

    //
    setState({
      editIgnoreRuleState: ActionState.INITIAL,
    });
  };
}

function deleteIgnoreRule({
  uuid,
  onSuccess,
  onError,
}: DeleteIgnoreRuleParams): Action<IgnoreStoreState> {
  return async ({ setState, getState }) => {
    if (getState().deleteIgnoreRuleState === ActionState.IN_PROGRESS) {
      return;
    }

    //
    try {
      setState({
        deleteIgnoreRuleState: ActionState.IN_PROGRESS,
      });
      await authenticatedDeleteFetch(`api/findings/ignore/${uuid}`);
      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to delete ignore rule");
      onError();
    }

    //
    setState({
      deleteIgnoreRuleState: ActionState.INITIAL,
    });
  };
}
// -----------------------------------------------------------------------------

function getCreateData({
  uuid,
  ignoreType,
  ruleType,
  assetRegex,
  ignoreReason,
}: CreateParams) {
  let data: CreateRuleData;
  if (ignoreType === "instance") {
    data = {
      finding_uuid: uuid,
      single_finding_ignored: true,
    };
  } else {
    switch (ruleType) {
      case "asset":
        data = {
          finding_uuid: uuid,
          asset_ignored: true,
        };
        break;
      case "finding":
        data = {
          finding_uuid: uuid,
          finding_type_ignored: true,
        };
        break;
      default:
        data = {
          finding_uuid: uuid,
          use_asset_re: true,
          asset_re: assetRegex,
        };
        break;
    }
  }

  if (ignoreReason) {
    data.ignore_reason = ignoreReason;
  }

  //
  return data;
}

// -----------------------------------------------------------------------------

interface IgnoreStoreState {
  ignoreRules: ApiRule[];

  ignoreState: ActionState;
  previewIgnoreState: ActionState;
  unignoreState: ActionState;
  bulkIgnoreState: ActionState;
  bulkUnignoreState: ActionState;
  listRulesState: ActionState;
  editIgnoreRuleState: ActionState;
  deleteIgnoreRuleState: ActionState;
}

export type IgnoreType = "instance" | "rule";
export type RuleType = "finding" | "asset" | "regex" | "";

interface CreateRuleDataBase {
  finding_uuid: string;
  ignore_reason?: string;
}
interface CreateSingleFindingRuleData extends CreateRuleDataBase {
  single_finding_ignored: boolean;
}

interface CreateFindingTypeRuleData extends CreateRuleDataBase {
  finding_type_ignored: boolean;
}

interface CreateAssetRuleData extends CreateRuleDataBase {
  asset_ignored: boolean;
}

interface CreateRegexRuleData extends CreateRuleDataBase {
  use_asset_re: boolean;
  asset_re: string;
}

type CreateRuleData =
  | CreateSingleFindingRuleData
  | CreateFindingTypeRuleData
  | CreateAssetRuleData
  | CreateRegexRuleData;

export interface ApiRule {
  uuid: string;
  affected_findings: string[];
  created_at: string;
  created_by: string;
  updated_at: string;
  ignore_reason: string | null;
  tk_org: string;

  // rule type
  asset_ignored: boolean;
  finding_type_ignored: boolean;
  single_finding_ignored: boolean;
  use_asset_re: boolean;

  // details
  asset_name: string | null;
  asset_re: string | null;
  finding_type_name: string | null;
  finding_uuid: string;
}

interface CreateParams {
  uuid: string;
  ignoreType: IgnoreType;
  ruleType: RuleType;
  assetRegex: string;
  ignoreReason: string;
}

interface IgnoreParams extends CreateParams {
  onSuccess: Function;
  onError: Function;
}

interface UnignoreParams {
  uuid: string;
  onSuccess: Function;
  onError: Function;
}

interface BulkIgnoreParams {
  uuids: string[];
  ignoreReason: string;
  onSuccess: Function;
  onError: Function;
}

interface BulkUnignoreParams {
  uuids: string[];
  onSuccess: Function;
  onError: Function;
}

interface EditIgnoreRuleParams {
  uuid: string;
  newReason: string;
  onSuccess: Function;
  onError: Function;
}

interface DeleteIgnoreRuleParams {
  uuid: string;
  onSuccess: Function;
  onError: Function;
}
